import React, { useState, useEffect, useRef } from 'react';
import { User, FolderOpen, Palette, Heart, Phone, HelpCircle, ChevronUp } from 'lucide-react';

const ExperiencePage = () => {
  const experiences = [
    {
      id: 'korecent',
      company: 'Korecent Solutions Pvt. Ltd.',
      designation: 'Software Developer Intern',
      duration: 'Jan 2025 – Jul 2025',
      location: 'Panchkula, Haryana',
      description: [
        'Led a team of five to build an event management platform, streamlining registration pipelines, real-time participant tracking, and secure payment processing to elevate operational performance.',
        'Implemented The Expense Manager, a modular finance tracking system supporting 36 languages with intelligent expense categorization, cross-platform reporting, and rule-based automation.',
        'Designed a scalable payroll and taxation engine that automated monthly compliance and salary disbursement workflows using structured backend logic.',
      ],
    },
    {
      id: 'bluemoon',
      company: 'Bluemoon Media',
      designation: 'Software Development Intern',
      duration: 'Apr 2024 – Jun 2024',
      location: 'Chandigarh, India',
      description: [
        'Developed ParsecLive.com, a full-stack event ticketing platform for a US-based client, tested by 15–20 users.',
        'Integrated JSON Web Token (JWT) authentication for secure, session-based login with 10-minute token expiry, enabling cross-device access and preventing duplicate sessions.',
        'Created an administrative dashboard using Multer for file management and managed cloud infrastructure with MongoDB Atlas.',
      ],
    },
  ];

  const navItems = [
    { icon: User, label: 'Me', active: false },
    { icon: FolderOpen, label: 'Projects', active: false },
    { icon: Palette, label: 'Skills', active: false },
    { icon: Heart, label: 'Experience', active: true },
    { icon: Phone, label: 'Contact', active: false },
    { icon: HelpCircle, label: '', active: false }
  ];

  const [messages, setMessages] = useState([]);
  const [currentInput, setCurrentInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const generateExperienceResponse = (userPrompt) => {
    let responseContent = "";

    const foundExperience = experiences.find(exp => userPrompt.includes(exp.company));

    if (foundExperience) {
      responseContent = `### **${foundExperience.company}**\n\n**Designation:** ${foundExperience.designation}\n**Duration:** ${foundExperience.duration}\n**Location:** ${foundExperience.location}\n\n**Responsibilities & Achievements:**\n${foundExperience.description.map(desc => `- ${desc}`).join('\n')}`;
    } else if (userPrompt.includes("Show me your work experience") || userPrompt.includes("show experience") || userPrompt.includes("work history") || userPrompt.includes("timeline of experience")) {
      responseContent = {
        type: 'experience-timeline',
        content: experiences.map(exp => ({
          id: exp.id,
          company: exp.company,
          designation: exp.designation,
          duration: exp.duration
        }))
      };
    } else {
      responseContent = "I can show you my work experience! Please ask me to 'Show me your work experience' or click on a company name once the timeline appears.";
    }
    return responseContent;
  };

  const sendMessage = (promptText, experienceData = null) => {
    setMessages((prevMessages) => [...prevMessages, { type: 'user', content: promptText }]);
    setCurrentInput('');
    setIsTyping(true);

    setTimeout(() => {
      let aiResponse;
      if (experienceData) {
        aiResponse = generateExperienceResponse(`Tell me about my experience at ${experienceData.company}`);
      } else {
        aiResponse = generateExperienceResponse(promptText);
      }
      setMessages((prevMessages) => [...prevMessages, { type: 'ai', content: aiResponse }]);
      setIsTyping(false);
    }, 2000);
  };

  const handleCompanyClick = (experienceId) => {
    const experience = experiences.find(exp => exp.id === experienceId);
    if (experience) {
      sendMessage(`Tell me about my experience at ${experience.company}`, experience);
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const autoPromptText = "Show me your work experience.";

    let charIndex = 0;
    const typingInterval = setInterval(() => {
      if (charIndex <= autoPromptText.length) {
        setCurrentInput(autoPromptText.substring(0, charIndex));
        charIndex++;
      } else {
        clearInterval(typingInterval);
        sendMessage(autoPromptText);
      }
    }, 50);

    return () => {
      clearInterval(typingInterval);
    };
  }, []);

  const renderContent = (messageContent) => {
    if (typeof messageContent === 'object' && messageContent.type === 'experience-timeline') {
      return (
        <div className="flex flex-col space-y-6 mt-4">
          {messageContent.content.map((exp, index) => (
            <div key={exp.id} className="relative pl-8 sm:pl-10">
              <div className="absolute left-0 top-0 mt-1.5 w-3 h-3 sm:w-4 sm:h-4 bg-blue-600 rounded-full ring-4 ring-blue-200"></div>
              {index < messageContent.content.length - 1 && (
                <div className="absolute left-[5px] sm:left-[7px] top-6 bottom-[-24px] w-0.5 bg-gray-300"></div>
              )}

              <div className="flex flex-col">
                <button
                  onClick={() => handleCompanyClick(exp.id)}
                  className="text-left text-blue-800 text-lg sm:text-xl font-bold hover:underline cursor-pointer focus:outline-none"
                >
                  {exp.company}
                </button>
                <p className="text-gray-700 text-base sm:text-lg font-semibold mt-1">{exp.designation}</p>
                <p className="text-gray-600 text-sm sm:text-base mt-0.5">{exp.duration}</p>
              </div>
            </div>
          ))}
        </div>
      );
    }

    return messageContent.split('\n').map((line, lineIndex) => {
      if (line.startsWith('## ')) {
        return <h2 key={lineIndex} className="text-xl sm:text-2xl md:text-3xl font-bold mt-2 sm:mt-3 mb-2 sm:mb-3">{line.substring(3)}</h2>;
      }
      if (line.startsWith('### ')) {
        const match = line.match(/### \*\*(.*?)\*\*/);
        if (match) {
          return (
            <h3 key={lineIndex} className="text-lg sm:text-xl font-bold mt-2 sm:mt-3 mb-1 sm:mb-2 text-blue-800">
              {match[1]}
            </h3>
          );
        }
        return <h3 key={lineIndex} className="text-lg sm:text-xl font-semibold mt-2 sm:mt-3 mb-1 sm:mb-2">{line.substring(4)}</h3>;
      }

      let formattedLine = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

      formattedLine = formattedLine.replace(/`(.*?)`/g, '<span class="px-1.5 py-0.5 sm:px-2 sm:py-1 bg-gray-700 text-white text-xs sm:text-sm font-medium rounded-md inline-block mr-0.5 my-0.5 sm:mr-1 sm:my-1">$1</span>');

      if (formattedLine.startsWith('- ')) {
        return <li key={lineIndex} dangerouslySetInnerHTML={{ __html: formattedLine.substring(2) }} className="ml-5 list-disc text-sm sm:text-base leading-normal sm:leading-relaxed text-justify"></li>;
      }
      return <p key={lineIndex} dangerouslySetInnerHTML={{ __html: formattedLine }} className="leading-normal sm:leading-relaxed text-sm sm:text-base"></p>;
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-800 flex flex-col text-sm sm:text-base">
      <div className="flex-grow mx-4 sm:mx-8 md:mx-12 lg:mx-[10%] py-6 sm:py-8 md:py-10 lg:py-12 overflow-y-auto">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8 sm:mb-10 md:mb-12 space-y-3 sm:space-y-4">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} gap-2 sm:gap-3`}
              >
                {message.type === 'ai' && (
                  <img
                    src="images/Siddharth_Singh_Khati.png"
                    alt="AI Profile"
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover self-start mb-1"
                  />
                )}
                <div
                  className={`max-w-[80%] sm:max-w-[70%] py-1.5 px-2.5 sm:py-2 sm:px-3 rounded-2xl sm:rounded-3xl text-sm sm:text-base ${
                    message.type === 'user'
                      ? 'bg-blue-900 text-white'
                      : 'text-gray-800'
                  }`}
                >
                  {renderContent(message.content)}
                </div>
                {message.type === 'user' && (
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-600 flex items-center justify-center text-white self-end mb-1">
                    You
                  </div>
                )}
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start gap-2 sm:gap-3">
                <img
                  src="images/Siddharth_Singh_Khati.png"
                  alt="AI Profile"
                  className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover self-start mb-1"
                />
                <div className="max-w-[70%] p-3 sm:p-4 rounded-xl bg-white border border-gray-200">
                  <div className="flex items-center space-x-1 sm:space-x-1.5">
                    <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gray-500 rounded-full animate-bounce delay-100"></span>
                    <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gray-500 rounded-full animate-bounce delay-200"></span>
                    <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-gray-500 rounded-full animate-bounce delay-300"></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 left-0 right-0 bg-gray-50 pt-3 sm:pt-4 border-t border-gray-100">
        <div className="mx-4 sm:mx-8 md:mx-12 lg:mx-[10%] pb-6 sm:pb-8">
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              <input
                type="text"
                placeholder="Ask me anything"
                className="w-full px-3.5 py-2.5 sm:px-4 sm:py-3 bg-gray-100 border border-gray-200 rounded-full text-sm sm:text-base text-gray-700 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={currentInput}
                readOnly
              />
              <button className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-blue-600 text-white p-1.5 sm:p-2 rounded-full hover:bg-blue-700 transition-colors">
                <ChevronUp className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExperiencePage;